/**************************************************
 * components/dash/PreventGrid.js
 *
 * @author  LSJ
 * @since   1.0 (2025-02-28)
 * @version 1.0
 **************************************************/
import { FileList } from "@components";
import { useFileUpload } from "@hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useState, useMemo, useEffect } from "react";
import "./Dashboard.css";

/**
 * PreventGrid
 *
 * "프레젠테이션 컴포넌트"로서, [예방 대책] 대시보드 그리드를 렌더링합니다.
 * 전달받은 props를 이용하여 [테이블 헤더, 본문, 푸터], 그리고 셀 병합(rowSpan) 등 처리를 수행합니다.
 *
 * @param {Array} data 예방 대책 데이터 (PreventDTO 리스트)
 */
const PreventGrid = ({ data, siteCodes, refetch, selectedSite = "" }) => {
  // gridData는 각 행의 데이터를 보유합니다.
  const [gridData, setGridData] = useState(data);
  // deletedFileMap는 각 grid row별 삭제된 파일 fileSn 배열을 관리합니다.
  const [deletedFileMap, setDeletedFileMap] = useState({});

  // data prop이 변경될 때 gridData 동기화
  useEffect(() => {
    setGridData(data);
  }, [data]);
  


  // 셀렉트 옵션: data에 존재하는 siteCode 목록 (숫자 → 오름차순, null은 'NULL'로 묶어 "미지정")
    const siteOptions = useMemo(() => {
      const options = [{ key: '', value: '전체 사업장' }];
      
      // Map<siteCode(string|"NULL"), siteName(string)>
      const map = new Map();
      for (const d of siteCodes) {
        const key = d.siteCode == null ? "NULL" : String(d.siteCode);
        const value =
          d.siteName && d.siteName.trim() !== ""
            ? d.siteName
            : d.siteCode == null
            ? "미지정"
            : String(d.siteCode);
        if (!map.has(key)) map.set(key, value);
      }
      // 정렬: 숫자 코드 → 오름차순, 그 외 문자열은 뒤로
      const arr = Array.from(map.entries()).map(([key, value]) => ({ key, value }));
      arr.sort((a, b) => {
        const na = Number(a.key),
          nb = Number(b.key);
        const ia = Number.isNaN(na),
          ib = Number.isNaN(nb);
        if (ia && ib) return a.value.localeCompare(b.value);
        if (ia) return 1;
        if (ib) return -1;
        return na - nb;
      });
      
      options.push(...arr);
      return options;
    }, [siteCodes]);
  
    const effectiveSelected = useMemo(
        () => selectedSite || (siteOptions[0]?.key ?? ""),
        [selectedSite, siteOptions]
      );
  
    // 렌더링용 데이터: 사업장 선택에 따른 필터링
    const filteredData = useMemo(() => {
      if (!effectiveSelected || effectiveSelected === '') return [];
      return gridData.filter((d) => {
        const key = d.siteCode == null ? "NULL" : Number(d.siteCode);
        return key === Number(effectiveSelected);
      });
    }, [gridData, effectiveSelected]);

  const queryClient = useQueryClient();
  const { mutate: uploadFile, isLoading } = useFileUpload();

  // 파일 업로드 후 응답 데이터로 상태 업데이트
  const handleUploadSuccess = (responseData) => {
    const { atchFileId, maccCode } = responseData.result;

    // 같은 SACC_CODE를 가진 모든 항목이 업데이트되므로 전체 데이터를 다시 가져옴
    if (refetch) {
      refetch();
    }

    setDeletedFileMap((prev) => ({ ...prev, [maccCode]: [] }));
    queryClient.invalidateQueries(["fileDetails", atchFileId ?? "", ""]);
  };

  // 신규 파일 선택 시 파일 업로드 API 호출
  const handleFileChange = (e, item) => {
    if (isLoading) return;
    const { maccCode, atchFileId, eqptCode, saccCode } = item;
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // FormData 생성 및 파일, 삭제된 파일 목록 추가
    const formPayload = new FormData();
    files.forEach((f) => formPayload.append("file", f));
    const itemDeletedFiles = deletedFileMap[maccCode] || [];
    itemDeletedFiles.forEach((sn) => {
      formPayload.append("deletedFileSns", sn);
    });

    // API 호출에 사용할 추가 파라미터 구성 (saccCode 추가)
    const params = { maccCode, atchFileId, eqptCode, saccCode };

    uploadFile(
      { formPayload, params },
      {
        onSuccess: (responseData) => {
          handleUploadSuccess(responseData);
        },
        onError: (error) => {
          console.error("파일 업로드 실패:", error);
        },
      }
    );

    // 같은 파일 재선택 시 onChange 트리거를 위해 input 초기화
    e.target.value = "";
  };

  // 데이터가 없으면 사용자에게 메시지 표시합니다.
  if (!data || data.length === 0) {
    return <div>데이터가 없습니다.</div>;
  }

  // 전체 사업장이 선택되었거나 필터링된 데이터가 없으면 메시지 표시
  if (!effectiveSelected || effectiveSelected === '' || filteredData.length === 0) {
    const message = !effectiveSelected || effectiveSelected === '' 
      ? "사업장을 선택하면 예방대책 정보를 확인할 수 있습니다."
      : "선택한 사업장에 표시할 데이터가 없습니다.";
      
    return (
        <div className="text-center py-4">{message}</div>
    );
  }

  return (
    <div className="og-wrap">
      <h2 className="cg-title">[중대재해 예방대책]</h2>
      {/* 테이블 본문(스크롤 및 드래그 기능 적용): 중대재해 발생 현장 */}
      <div className="og-table-wrap">
        {/* 커스텀 테이블 */}
        <table className="og-prevent-table">
          {/* colgroup: 각 열의 너비를 지정 */}
          <colgroup>
            <col className="w-2" />
            <col className="w-3" />
            <col className="w-7" />
            <col className="w-8" />
            <col className="w-2" />
          </colgroup>
          {/* thead: */}
          <thead>
            {/* 첫 번째 헤더 행: 공정명, 설비/장비, 중대재해 발생형태, 예방 대책, 버튼 */}
            <tr>
              <th style={{ background: 'var(--dash-accent-light)', color: 'var(--dash-text-secondary)' }}>공정명</th>
              <th style={{ background: 'var(--dash-accent-light)', color: 'var(--dash-text-secondary)' }}>설비/장비</th>
              <th style={{ background: 'var(--dash-danger-light)', color: 'var(--dash-danger)' }}>중대재해 발생형태</th>
              <th style={{ background: 'var(--dash-success-light)', color: 'var(--dash-success)' }} colSpan={2}>
                예방 대책
              </th>
            </tr>
          </thead>
          {/* tbody: */}
          <tbody>
            {filteredData.map((item) => (
              <tr key={item.maccCode}>
                <td>{item.procName}</td>
                <td style={{ color: 'var(--dash-accent)' }}>{item.eqptName}</td>
                <td style={{ color: 'var(--dash-danger)' }}>{item.saccName}</td>
                <td>
                  {/* FileList: 삭제되지 않았고 업로드 모드가 해제된 경우에 렌더링 */}
                  <FileList
                    atchFileId={item.atchFileId}
                    showDelete={true}
                    className="edit"
                    deletedFileSns={deletedFileMap[item.maccCode] || []}
                    onFileDeleteMark={(file) => {
                      // 삭제 버튼(X) 클릭 시: 해당 파일의 fileSn을 삭제 목록에 추가하고 업로드 모드 활성화
                      setDeletedFileMap((prev) => ({
                        ...prev,
                        [item.maccCode]: [...(prev[item.maccCode] || []), file.fileSn],
                      }));
                    }}
                  />
                </td>
                <td>
                  {/* 파일 업로드 UI 영역 */}
                  <div className="file-box justify-content-center">
                    <label htmlFor={item.maccCode} className="og-upload-btn" style={{ margin: 0 }}>
                      파일 업로드
                    </label>
                    <input type="file" id={item.maccCode} name="file" className="upload-hd" style={{ display: "none" }} disabled={isLoading} onChange={(e) => handleFileChange(e, item)} multiple />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PreventGrid;
